import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { startExtractJob, pollExtractJob, mergeReference } from "@/lib/extractJob";
import { deliveryDraftKey, loadDraft, saveDraft, clearDraft } from "@/lib/deliveryDraft";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, EmptyState, fmtDateTime } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import PhotoUpload from "@/components/PhotoUpload";
import DocumentScanner from "@/components/DocumentScanner";
import { LineReview, ReadingSummary } from "@/components/DeliveryLineReview";
import { toast } from "sonner";
import {
  DOC_TYPES, CHECK_ITEMS, TEMP_PRESETS, tempPassed, hasException, needsCorrectiveAction,
  mapAiProposalToReference, documentTemperatureNote, acceptanceLabel, canConfirm, mapProposalLines,
  buildLearnPayload, learnSummaryText, describeExtractError,
} from "@/lib/deliveries";
import {
  Truck, Plus, Sparkles, Check, X, AlertTriangle, CheckCircle2, XCircle, Thermometer, FileText, ChevronRight, Trash2,
} from "lucide-react";

const genOpId = () => (crypto?.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

// Only an AI-provenance label: nothing here is "human confirmed" until the chef saves the delivery.
function AiSuggestedTag() {
  return (
    <span className="text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded bg-violet-100 text-violet-700" data-testid="delivery-ai-suggested-tag">
      AI suggested — check
    </span>
  );
}

function TempRow({ t, onChange, onRemove }) {
  const p = tempPassed(t);
  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid="delivery-temp-row">
      <div className="flex items-center gap-2">
        <Input value={t.label} onChange={(e) => onChange({ ...t, label: e.target.value })} placeholder="Item (e.g. Chilled)" className="h-11 flex-1" data-testid="delivery-temp-label" />
        <Input type="number" inputMode="decimal" value={t.temperature} onChange={(e) => onChange({ ...t, temperature: e.target.value })} placeholder="°C" className="h-11 w-24 text-lg font-bold" data-testid="delivery-temp-value" />
        <button type="button" onClick={onRemove} data-testid="delivery-temp-remove" className="text-slate-400 hover:text-red-500 p-2"><Trash2 size={16} /></button>
      </div>
      <div className="flex items-center gap-2 mt-2">
        <Input type="number" value={t.minimum} onChange={(e) => onChange({ ...t, minimum: e.target.value })} placeholder="Min" className="h-9 w-20 text-sm" />
        <Input type="number" value={t.maximum} onChange={(e) => onChange({ ...t, maximum: e.target.value })} placeholder="Max" className="h-9 w-20 text-sm" />
        {p === true && <span className="text-xs text-brand-dark flex items-center gap-1"><Check size={13} /> in range</span>}
        {p === false && <span className="text-xs text-red-600 flex items-center gap-1 font-semibold"><AlertTriangle size={13} /> out of range</span>}
      </div>
      {p === false && (
        <Textarea value={t.comments || ""} onChange={(e) => onChange({ ...t, comments: e.target.value })} data-testid="delivery-temp-comment"
          placeholder="Corrective action for this reading (required)" className="mt-2 text-sm" />
      )}
    </div>
  );
}

function ReceiveDeliveryModal({ suppliers, aiAvailable, siteId, onClose, onSaved }) {
  const { user } = useAuth();
  const site = siteId && siteId !== "all" ? siteId : undefined;
  const liveKey = deliveryDraftKey(user, site);
  const [draftKey] = useState(liveKey); // frozen: a site/account switch never writes this form into another scope
  const [d0] = useState(() => loadDraft(draftKey) || {}); // restored in-progress delivery (user, org, site)
  const [stage, setStage] = useState("form"); // form | review
  const [fileId, setFileId] = useState(d0.fileId || null);
  const [scanIds, setScanIds] = useState(d0.scanIds || []);
  const [showScanner, setShowScanner] = useState(false);
  const [appendPages, setAppendPages] = useState(false);
  const [scanInfo, setScanInfo] = useState(d0.scanInfo || null);
  const [docText, setDocText] = useState(d0.docText || "");
  const [extracting, setExtracting] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [showPaste, setShowPaste] = useState(false);
  const [lines, setLines] = useState(d0.lines || []);
  const [ai, setAi] = useState(d0.ai || null); // sanitized proposal provenance
  const [docTempNote, setDocTempNote] = useState(d0.docTempNote ?? null);
  const [supplierId, setSupplierId] = useState(d0.supplierId || "");
  const [supplierName, setSupplierName] = useState(d0.supplierName || "");
  const [ref, setRef] = useState(d0.ref || { document_number: "", document_date: "", document_type: "", notes: "" });
  const [checks, setChecks] = useState(d0.checks || { packaging_ok: null, labels_dates_ok: null, damage: null, contamination: null, notes: "" });
  const [temps, setTemps] = useState(d0.temps || []);
  const [acceptance, setAcceptance] = useState(d0.acceptance || "accepted");
  const [correctiveAction, setCorrectiveAction] = useState(d0.correctiveAction || "");
  const [saving, setSaving] = useState(false);
  const [opId] = useState(() => d0.opId || genOpId());
  const [job, setJob] = useState(d0.job || null); // { client_key, job_id } of the ONE logical extraction in flight
  const touched = useRef(new Set(d0.touched || [])); // fields the chef edited (incl. deliberately cleared)
  const [, bump] = useState(0);
  const touch = (f) => { if (!touched.current.has(f)) { touched.current.add(f); bump((n) => n + 1); } };
  const discarded = useRef(false);

  useEffect(() => {
    if (discarded.current) return;
    saveDraft(draftKey, { fileId, scanIds, scanInfo, docText, lines, ai, docTempNote, supplierId, supplierName, ref, checks,
      temps, acceptance, correctiveAction, opId, job, touched: [...touched.current] });
  });
  const discard = () => { discarded.current = true; clearDraft(draftKey); onClose(); };
  useEffect(() => {
    if (liveKey !== draftKey) onClose(); // switched site/account: keep this draft under ITS scope and close
  }, [liveKey, draftKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const exception = hasException(temps);
  const mustCorrect = needsCorrectiveAction(acceptance, temps);
  const confirmable = canConfirm({ acceptance, supplierName, temps, correctiveAction });

  const closed = useRef(false);

  // Applies a proposal WITHOUT overwriting anything the chef typed while extraction was running.
  const applyResult = (data) => {
    if (data.combined_file_id) setFileId(data.combined_file_id); // the preserved, ordered scan is the evidence document
    setScanInfo({ pages: data.page_count, dupes: (data.duplicate_pages || []).length, totals: data.totals_reconciliation?.status, duplicate: data.possible_duplicate, attention: data.fields_needing_attention || [], passes: data.extraction_passes || null,
      notes: data.review_notes || [], excluded: data.excluded_rows || [], incomplete: !!data.incomplete, duplicateDelivery: data.possible_duplicate_delivery || null, recon: data.totals_reconciliation || null });
    if (data.status === "unsupported") {
      setAi({ feature: "delivery_extraction", status: "unsupported", proposed: {} });
      toast.warning(data.message || "That document can't be read by AI — enter details manually or paste the text");
      setShowPaste(true);
      return;
    }
    const proposed = data.proposal || {};
    setAi({ feature: "delivery_extraction", status: "suggested", proposed });
    const mapped = mapAiProposalToReference(proposed);
    const t = touched.current;
    if (!t.has("supplier_name")) setSupplierName((s) => mapped.supplier_name || s);
    setRef((r) => mergeReference(r, mapped, t));
    setDocTempNote(documentTemperatureNote(proposed));
    if (!t.has("lines")) setLines(mapProposalLines(proposed));
    else {
      const found = mapProposalLines(proposed);
      setLines((cur) => {
        if (cur.some((l) => (l.description || "").trim())) {
          if (found.length) toast.info(`AI read ${found.length} row(s) — your edited lines were kept. Clear them to use the AI rows.`);
          return cur;
        }
        return found.length ? found : cur; // only empty rows were added: use what AI read
      });
    }
    toast.success("AI suggested details — please review and correct every value");
  };

  // AI is an accelerator, never a dependency: keep everything entered and explain the actual failure.
  const applyFailure = (norm) => {
    setAi({ feature: "delivery_extraction", status: "failed", proposed: {} });
    const err = describeExtractError(norm);
    setAiError(err);
    toast.warning(err.message);
  };

  const followJob = async (jobId, startedAt) => {
    setExtracting(true);
    setAiError(null);
    try {
      const res = await pollExtractJob(jobId, { isCancelled: () => closed.current, startedAt });
      if (res.status === "cancelled") return; // form closed: the job continues and is resumed on reopen
      if (closed.current) return;
      setJob(null); // final result: the next extraction is a deliberate NEW one (new key)
      if (res.status === "done") applyResult(res.result);
      else applyFailure({ status: res.error?.status, code: res.error?.code, message: res.error?.msg, reference: res.error?.reference });
    } catch (e) {
      if (closed.current) return;
      setJob(null);
      applyFailure(e?.normalized);
    } finally { if (!closed.current) setExtracting(false); }
  };

  // Submit (or re-submit after a lost response) the SAME logical extraction with the SAME key.
  const submitAndFollow = async (clientKey, ids) => {
    setExtracting(true);
    setAiError(null);
    try {
      const j = await startExtractJob({ fileId: ids.fileId, scanIds: ids.scanIds, siteId: site, clientKey });
      if (closed.current) return;
      setJob({ client_key: clientKey, job_id: j.job_id, created_at: j.created_at });
      await followJob(j.job_id, j.created_at);
    } catch (e) {
      if (closed.current) return;
      const s = e?.normalized?.status;
      if (s && s !== 0 && s < 500) setJob(null); // rejected: the key is spent. Network/5xx: keep it for retry
      applyFailure(e?.normalized);
      setExtracting(false);
    }
  };

  useEffect(() => {
    closed.current = false;
    if (d0.job?.job_id) followJob(d0.job.job_id, d0.job.created_at);
    else if (d0.job?.client_key) submitAndFollow(d0.job.client_key, { fileId: d0.fileId, scanIds: d0.scanIds });
    return () => { closed.current = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const runExtract = async () => {
    if (!fileId && !scanIds.length && !docText.trim()) { toast.error("Add a delivery photo (or paste text) to extract"); return; }
    if (fileId || scanIds.length) {
      // PRIMARY: background job + short status polls. Reuse the pending key if a submission never got its answer.
      const key = job?.client_key || genOpId();
      setJob({ client_key: key, job_id: null }); // persisted BEFORE the request, so a lost response is retried with this key
      await submitAndFollow(key, { fileId, scanIds });
      return;
    }
    setExtracting(true);
    setAiError(null);
    try {
      const { data } = await api.post("/ai/extract", { feature: "delivery_extraction", text: docText, site_id: site });
      applyResult(data);
    } catch (e) {
      applyFailure(e?.normalized);
    } finally { setExtracting(false); }
  };

  const addTemp = (preset) => setTemps((ts) => [...ts, { label: preset?.label || "", temperature: "", minimum: preset?.minimum ?? "", maximum: preset?.maximum ?? "", comments: "" }]);

  const pickSupplier = (v) => {
    touch("supplier_name");
    setSupplierId(v);
    const s = suppliers.find((x) => x.supplier_id === v);
    if (s) setSupplierName(s.name);
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const body = {
        operation_id: opId, acceptance,
        venue_id: siteId && siteId !== "all" ? siteId : undefined,
        supplier_id: supplierId || undefined, supplier_name: supplierName,
        file_id: fileId || undefined,
        reference: { document_number: ref.document_number, document_date: ref.document_date, document_type: ref.document_type || undefined, notes: ref.notes },
        checks: { packaging_ok: checks.packaging_ok, labels_dates_ok: checks.labels_dates_ok, damage: checks.damage, contamination: checks.contamination, notes: checks.notes },
        temperatures: temps.map((t) => ({ label: t.label, temperature: Number(t.temperature), minimum: t.minimum === "" ? undefined : Number(t.minimum), maximum: t.maximum === "" ? undefined : Number(t.maximum), comments: t.comments })),
        corrective_action: correctiveAction || undefined,
        ai: ai ? { feature: ai.feature, status: ai.status, proposed: ai.proposed } : undefined,
        learn: buildLearnPayload({ supplierId, supplierName, lines }),
      };
      const { data } = await api.post("/deliveries", body);
      discarded.current = true;
      clearDraft(draftKey);
      toast.success(data.delivery.acceptance === "rejected" ? "Delivery rejected & logged" : "Delivery received ✓");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the delivery");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent data-testid="receive-delivery-modal" className="max-h-[92vh] overflow-y-auto max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Truck size={18} className="text-brand" /> Receive delivery</DialogTitle>
          <DialogDescription className="sr-only">Scan or enter a delivery, record receiving checks and temperatures, then confirm.</DialogDescription>
        </DialogHeader>

        {stage === "form" && (
          <div className="space-y-5">
            {/* STEP 1 — DOCUMENT */}
            <section className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400">1 · Document</div>
              {!showScanner ? (
                <>
                  <PhotoUpload value={fileId} onChange={(v) => { setFileId(v); setScanIds([]); }} label="Photograph delivery note / invoice" testid="delivery-doc-photo" />
                  <button type="button" onClick={() => setShowScanner(true)} data-testid="delivery-open-scanner" className="text-xs text-brand underline">Multi-page document? Scan several pages</button>
                </>
              ) : (
                <DocumentScanner sessionKey={`delivery-${opId}`} testid="delivery-scanner"
                  onComplete={(ids, n) => {
                    const all = appendPages ? [...(scanIds.length ? scanIds : fileId ? [fileId] : []), ...ids] : ids;
                    setScanIds(all); setFileId(all[0]); setShowScanner(false); setAppendPages(false);
                    toast.success(appendPages ? `${n} page(s) added — ${all.length} pages in this document. Extract again to read them together.` : `${n} page(s) ready — extract with AI or continue manually`);
                  }} />
              )}
              {scanIds.length > 0 && !showScanner && <div className="text-xs text-slate-600" data-testid="delivery-scan-summary">{scanIds.length}-page scan attached</div>}
              {scanInfo && (scanInfo.pages > 1 || scanInfo.totals === "mismatch" || scanInfo.duplicate || scanInfo.attention?.length > 0 || scanInfo.passes || scanInfo.notes?.length > 0 || scanInfo.duplicateDelivery) && (
                <div className="text-xs rounded-lg bg-slate-50 border p-2 space-y-0.5" data-testid="delivery-extract-info">
                  {scanInfo.pages > 1 && <div>{scanInfo.pages} pages read as one document{scanInfo.dupes ? ` · ${scanInfo.dupes} duplicate page(s) ignored` : ""}</div>}
                  {scanInfo.duplicateDelivery && <div className="text-red-700 font-semibold" data-testid="delivery-duplicate-delivery">A delivery with this invoice number was already recorded{scanInfo.duplicateDelivery.received_at ? ` on ${new Date(scanInfo.duplicateDelivery.received_at).toLocaleDateString()}` : ""} — don't import it twice.</div>}
                  {(scanInfo.notes || []).map((n) => <div key={n.code} className={n.code === "no_totals" ? "text-slate-600" : "text-amber-800"} data-testid={`delivery-note-${n.code}`}>{n.message}</div>)}
                  {scanInfo.recon?.status === "matches" && <div className="text-emerald-700" data-testid="delivery-totals-match">Lines, net, VAT and gross totals agree (net £{Number(scanInfo.recon.net_total).toFixed(2)}{scanInfo.recon.vat_total != null ? `, VAT £${Number(scanInfo.recon.vat_total).toFixed(2)}` : ""}{scanInfo.recon.gross_total != null ? `, gross £${Number(scanInfo.recon.gross_total).toFixed(2)}` : ""})</div>}
                  {scanInfo.excluded?.length > 0 && <details data-testid="delivery-excluded-rows"><summary className="cursor-pointer text-slate-500">{scanInfo.excluded.length} non-product row(s) not imported (sub-totals, headings, notes)</summary>
                    <ul className="pl-4 list-disc text-slate-500">{scanInfo.excluded.map((x, k) => <li key={k}>{x.reason.replace(/_/g, " ")}: {x.name || "—"}{x.line_net != null ? ` (£${Number(x.line_net).toFixed(2)})` : ""}</li>)}</ul></details>}
                  {(scanInfo.incomplete || scanInfo.notes?.some((n) => n.code === "missing_pages")) && !showScanner && <button type="button" onClick={() => { setAppendPages(true); setShowScanner(true); }} data-testid="delivery-add-missing-page" className="text-brand font-semibold underline">Add the missing page</button>}
                  {scanInfo.totals === "mismatch" && <div className="text-amber-700" data-testid="delivery-totals-mismatch">Line totals don't match the document total — check every line</div>}
                  {scanInfo.duplicate && <div className="text-red-700" data-testid="delivery-possible-duplicate">This document was already extracted on {new Date(scanInfo.duplicate.first_extracted_at).toLocaleDateString()} — check it isn't a duplicate</div>}
                  {scanInfo.attention.filter((a) => !(a === "line_items" && lines.length) && a !== "missing_pages").length > 0 && <div className="text-slate-500">Not found / needs checking: {scanInfo.attention.filter((a) => !(a === "line_items" && lines.length) && a !== "missing_pages").map((a) => (a === "temperature_c" ? "printed temperature" : a)).join(", ").replace(/_/g, " ")}</div>}
                  {scanInfo.passes && <ReadingSummary p={scanInfo.passes} />}
                </div>
              )}
              {aiAvailable && (
                <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-3 space-y-2" data-testid="delivery-ai-panel">
                  <div className="flex items-center gap-1.5 text-violet-700 text-xs font-semibold"><Sparkles size={14} /> Extract with AI</div>
                  <Button type="button" size="sm" onClick={runExtract} disabled={extracting || (!fileId && !scanIds.length && !docText.trim())} data-testid="delivery-ai-extract" className="w-full bg-brand hover:bg-brand-hover">
                    {extracting ? "Reading document…" : "Extract document with AI"}
                  </Button>
                  {aiError && (
                    <div className="text-xs rounded-lg border border-amber-200 bg-amber-50 p-2 text-amber-800" data-testid="delivery-ai-error">
                      {aiError.message} Your photo and anything you've entered are kept.
                      {aiError.reference && <span className="block text-[11px] text-amber-700/80" data-testid="delivery-ai-error-ref">Reference: {aiError.reference}</span>}
                    </div>
                  )}
                  <p className="text-[11px] text-violet-700/80">AI suggestions are unverified — review every value. A temperature printed on a document is not a food-safety reading.</p>
                  {!showPaste ? (
                    <button type="button" onClick={() => setShowPaste(true)} data-testid="delivery-ai-fallback-toggle" className="text-[11px] text-violet-700 underline">Can't read it? Paste the text instead</button>
                  ) : (
                    <Textarea value={docText} onChange={(e) => setDocText(e.target.value)} data-testid="delivery-ai-text" placeholder="Fallback: paste the delivery note / invoice text" className="text-sm" />
                  )}
                </div>
              )}
            </section>

            {/* STEP 2 — REVIEW/CORRECT HEADER */}
            <section className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400 flex items-center gap-2">2 · Supplier & document {ai?.status === "suggested" && <AiSuggestedTag />}</div>
              {suppliers.length > 0 && (
                <Select value={supplierId} onValueChange={pickSupplier}>
                  <SelectTrigger data-testid="delivery-supplier-select"><SelectValue placeholder="Pick a supplier (optional)" /></SelectTrigger>
                  <SelectContent>{suppliers.map((s) => <SelectItem key={s.supplier_id} value={s.supplier_id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Input value={supplierName} onChange={(e) => { touch("supplier_name"); setSupplierName(e.target.value); setSupplierId(""); }} data-testid="delivery-supplier-name" placeholder="Supplier name" className="h-11" />
              <div className="grid grid-cols-2 gap-2">
                <Input value={ref.document_number} onChange={(e) => { touch("document_number"); setRef({ ...ref, document_number: e.target.value }); }} data-testid="delivery-doc-number" placeholder="Document no." className="h-11" />
                <Input value={ref.document_date} onChange={(e) => { touch("document_date"); setRef({ ...ref, document_date: e.target.value }); }} data-testid="delivery-doc-date" placeholder="Date (e.g. 2026-06-01)" className="h-11" />
              </div>
              <Select value={ref.document_type} onValueChange={(v) => { touch("document_type"); setRef({ ...ref, document_type: v }); }}>
                <SelectTrigger data-testid="delivery-doc-type"><SelectValue placeholder="Document type (optional)" /></SelectTrigger>
                <SelectContent>{DOC_TYPES.map((d) => <SelectItem key={d.v} value={d.v}>{d.l}</SelectItem>)}</SelectContent>
              </Select>
              {docTempNote != null && (
                <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-2" data-testid="delivery-doc-temp-note">
                  Printed on document: {docTempNote}°C — <span className="font-semibold">reference only, not a food-safety reading.</span> Record actual temperatures below.
                </div>
              )}
              {lines.length === 0 && <button type="button" className="text-xs text-brand font-semibold flex items-center gap-1" onClick={() => { touch("lines"); setLines([{ canonical_name: "", description: "", sku: "", pack_size: "", unit: "", price: null, vat_rate: null, quantity: null, product_id: null, create_product: true, review: null, added_by_user: true }]); }} data-testid="delivery-add-first-line"><Plus size={12} /> Add an item row</button>}
              {lines.length > 0 && (
                <div className="rounded-xl border border-slate-200 p-2 space-y-1.5" data-testid="delivery-lines">
                  <div className="text-[11px] font-semibold text-slate-500">Extracted items ({lines.length}) — review before confirming</div>
                  {lines.map((l, i) => (
                    <div key={i} className="space-y-1">
                    <div className="flex flex-wrap sm:flex-nowrap items-center gap-2 text-sm" data-testid={`delivery-line-${i}`}>
                      <span className={`inline-flex items-center rounded-full text-[10px] px-1.5 py-0.5 shrink-0 ${l.not_delivered ? "bg-slate-200 text-slate-600" : "bg-violet-100 text-violet-700"}`} data-testid={`delivery-line-status-${i}`}>{l.not_delivered ? "Not delivered" : l.product_id ? "Matched" : "New product"}</span>
                      <Input value={l.description} onChange={(e) => { touch("lines"); setLines(lines.map((x, j) => j === i ? { ...x, description: e.target.value, canonical_name: e.target.value } : x)); }} data-testid={`delivery-line-name-${i}`} className="h-9 flex-1 min-w-[60%] sm:min-w-0" />
                      <Input value={l.quantity ?? ""} onChange={(e) => { touch("lines"); setLines(lines.map((x, j) => j === i ? { ...x, quantity: e.target.value === "" ? null : Number(e.target.value) } : x)); }} inputMode="decimal" placeholder="Qty" title="Quantity delivered" className={`h-9 w-14 shrink-0 ${l.review?.uncertain?.some((k) => /qty|quantity/.test(k)) ? "border-amber-500" : ""}`} data-testid={`delivery-line-qty-${i}`} />
                      <Input value={l.price ?? ""} onChange={(e) => { touch("lines"); setLines(lines.map((x, j) => j === i ? { ...x, price: e.target.value === "" ? null : Number(e.target.value) } : x)); }} inputMode="decimal" placeholder="£" className="h-9 w-16 shrink-0" data-testid={`delivery-line-price-${i}`} />
                      <button type="button" onClick={() => { touch("lines"); setLines(lines.filter((_, j) => j !== i)); }} data-testid={`delivery-line-remove-${i}`} className="text-slate-400 hover:text-red-500 p-1 shrink-0"><Trash2 size={14} /></button>
                    </div>
                    <LineReview l={l} i={i} onAccept={() => { touch("lines"); setLines(lines.map((x, j) => j === i ? { ...x, review: { ...x.review, accepted: true } } : x)); }} />
                    </div>
                  ))}
                  <button type="button" className="text-xs text-brand font-semibold flex items-center gap-1 pt-1" onClick={() => { touch("lines"); setLines([...lines, { canonical_name: "", description: "", sku: "", pack_size: "", unit: "", price: null, vat_rate: null, quantity: null, product_id: null, create_product: true, review: null, added_by_user: true }]); }} data-testid="delivery-add-missing-line"><Plus size={12} /> Add a missing row</button>
                </div>
              )}
            </section>

            {/* STEP 3 — RECEIVING CHECKS */}
            <section className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400">3 · Receiving checks</div>
              <div className="grid grid-cols-2 gap-2">
                {CHECK_ITEMS.map((c) => {
                  const val = checks[c.k];
                  const active = val === true;
                  return (
                    <button key={c.k} type="button" data-testid={`delivery-check-${c.k}`}
                      onClick={() => setChecks({ ...checks, [c.k]: active ? null : true })}
                      className={`text-left text-sm rounded-xl border p-3 transition-colors ${active ? (c.good ? "border-brand bg-brand-light/40" : "border-red-300 bg-red-50") : "border-slate-200"}`}>
                      <span className="flex items-center gap-2">
                        {active ? (c.good ? <Check size={15} className="text-brand-dark" /> : <AlertTriangle size={15} className="text-red-500" />) : <span className="h-3.5 w-3.5 rounded border border-slate-300" />}
                        {c.l}
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>

            {/* STEP 4 — TEMPERATURES */}
            <section className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400 flex items-center gap-1.5"><Thermometer size={13} /> 4 · Temperatures (observed)</div>
              {temps.map((t, i) => <TempRow key={i} t={t} onChange={(nt) => setTemps(temps.map((x, j) => (j === i ? nt : x)))} onRemove={() => setTemps(temps.filter((_, j) => j !== i))} />)}
              <div className="flex gap-2 flex-wrap">
                {TEMP_PRESETS.map((p) => <Button key={p.label} type="button" size="sm" variant="outline" data-testid={`delivery-add-temp-${p.label.toLowerCase()}`} onClick={() => addTemp(p)}><Plus size={13} className="mr-1" />{p.label}</Button>)}
                <Button type="button" size="sm" variant="ghost" data-testid="delivery-add-temp" onClick={() => addTemp()}><Plus size={13} className="mr-1" />Other</Button>
              </div>
            </section>

            {/* STEP 5 — DECISION */}
            <section className="space-y-2">
              <div className="text-xs font-bold uppercase tracking-wide text-slate-400">5 · Decision</div>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="delivery-accept" onClick={() => setAcceptance("accepted")} className={`rounded-xl border p-3 font-semibold flex items-center justify-center gap-2 ${acceptance === "accepted" ? "border-brand bg-brand-light/40 text-brand-dark" : "border-slate-200 text-slate-500"}`}><CheckCircle2 size={17} /> Accept</button>
                <button type="button" data-testid="delivery-reject" onClick={() => setAcceptance("rejected")} className={`rounded-xl border p-3 font-semibold flex items-center justify-center gap-2 ${acceptance === "rejected" ? "border-red-400 bg-red-50 text-red-600" : "border-slate-200 text-slate-500"}`}><XCircle size={17} /> Reject</button>
              </div>
              {mustCorrect && (
                <Textarea value={correctiveAction} onChange={(e) => setCorrectiveAction(e.target.value)} data-testid="delivery-corrective-action"
                  placeholder={acceptance === "rejected" ? "Reason for rejection (required)" : "Corrective action taken (required for out-of-range)"} className="text-sm" />
              )}
            </section>

            <DialogFooter>
              <Button variant="outline" onClick={discard} data-testid="delivery-cancel">Discard</Button>
              <Button className="bg-brand hover:bg-brand-hover" data-testid="delivery-review" disabled={!confirmable} onClick={() => setStage("review")}>Review <ChevronRight size={16} className="ml-1" /></Button>
            </DialogFooter>
          </div>
        )}

        {stage === "review" && (
          <div className="space-y-4" data-testid="delivery-review-panel">
            <div className={`rounded-xl p-3 text-sm font-semibold flex items-center gap-2 ${acceptance === "rejected" ? "bg-red-50 text-red-600" : exception ? "bg-amber-50 text-amber-700" : "bg-brand-light/50 text-brand-dark"}`}>
              {acceptance === "rejected" ? <XCircle size={18} /> : exception ? <AlertTriangle size={18} /> : <CheckCircle2 size={18} />}
              {acceptanceLabel(acceptance)}{exception ? " — temperature exception" : ""}
            </div>
            <div className="text-sm space-y-1.5">
              <div className="flex justify-between"><span className="text-slate-500">Supplier</span><span className="font-medium flex items-center gap-1.5">{supplierName} {ai?.status === "suggested" && <AiSuggestedTag />}</span></div>
              {ref.document_number && <div className="flex justify-between"><span className="text-slate-500">Document</span><span className="font-medium">{ref.document_number}{ref.document_date ? ` · ${ref.document_date}` : ""}</span></div>}
              <div className="flex justify-between"><span className="text-slate-500">Checks</span><span className="font-medium">{CHECK_ITEMS.filter((c) => checks[c.k] === true).map((c) => c.l).join(", ") || "—"}</span></div>
            </div>
            {temps.length > 0 && (
              <div className="space-y-1.5" data-testid="delivery-review-temps">
                <div className="text-xs font-bold uppercase tracking-wide text-slate-400">Observed temperatures (human recorded)</div>
                {temps.map((t, i) => (
                  <div key={i} className="flex justify-between text-sm"><span>{t.label || "Item"}</span>
                    <span className={tempPassed(t) === false ? "text-red-600 font-semibold" : "text-brand-dark font-medium"}>{t.temperature}°C {tempPassed(t) === false ? "· exception" : ""}</span></div>
                ))}
              </div>
            )}
            {mustCorrect && <div className="text-sm bg-slate-50 border border-slate-200 rounded-lg p-2.5"><span className="text-slate-500">Corrective action / reason:</span> {correctiveAction}</div>}
            {(() => { const s = learnSummaryText({ supplierId, supplierName, lines }); return s.length > 0 ? (
              <div className="rounded-xl border border-violet-200 bg-violet-50/40 p-2.5 text-xs text-violet-800" data-testid="delivery-learn-summary">
                <div className="font-semibold mb-1">This delivery will:</div>
                <ul className="list-disc pl-4 space-y-0.5">{s.map((x, i) => <li key={i}>{x}</li>)}</ul>
              </div>
            ) : null; })()}
            {ai && <div className="text-[11px] text-slate-400">Some header fields were AI-suggested and human-confirmed. Temperatures and the accept/reject decision are entirely human-recorded.</div>}
            <DialogFooter>
              <Button variant="outline" onClick={() => setStage("form")} data-testid="delivery-back">Back</Button>
              <Button className="bg-brand hover:bg-brand-hover" data-testid="delivery-confirm" disabled={saving || !confirmable} onClick={confirm}>{saving ? "Saving…" : "Confirm delivery"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Deliveries() {
  const { has } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [deliveries, setDeliveries] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [aiAvailable, setAiAvailable] = useState(false);
  const [filter, setFilter] = useState("all");
  const [receiving, setReceiving] = useState(false);
  const canRecord = has("create_deliveries");
  const [siteId, setSiteId] = useState(() => localStorage.getItem("cc_site") || "all");
  useEffect(() => {
    const h = () => setSiteId(localStorage.getItem("cc_site") || "all"); // site-switcher: the open form closes (own draft scope)
    window.addEventListener("cc-site-changed", h);
    return () => window.removeEventListener("cc-site-changed", h);
  }, []);

  const load = async () => {
    const [d] = await Promise.all([api.get("/deliveries")]);
    setDeliveries(d.data);
  };
  useEffect(() => {
    load();
    api.get("/suppliers").then((r) => setSuppliers(r.data || [])).catch(() => setSuppliers([]));
    api.get("/ai/features").then((r) => setAiAvailable((r.data.features || []).includes("delivery_extraction"))).catch(() => setAiAvailable(false));
  }, []);
  useEffect(() => { if (params.get("receive") === "1" && canRecord) { setReceiving(true); setParams({}, { replace: true }); } }, [params, canRecord]);

  const filtered = useMemo(() => {
    if (filter === "exceptions") return deliveries.filter((d) => d.has_temperature_exception || d.acceptance === "rejected");
    if (filter === "accepted" || filter === "rejected") return deliveries.filter((d) => d.acceptance === filter);
    return deliveries;
  }, [deliveries, filter]);

  const onSaved = () => { setReceiving(false); load(); };

  return (
    <div>
      <PageHeader title="Deliveries" subtitle="Receive goods-in with document capture, checks and temperatures — signed and audit-ready." testid="deliveries-header"
        action={canRecord && <Button className="bg-brand hover:bg-brand-hover rounded-xl" data-testid="receive-delivery-btn" onClick={() => setReceiving(true)}><Truck size={18} className="mr-1.5" /> Receive delivery</Button>} />

      {canRecord && (
        <button onClick={() => setReceiving(true)} data-testid="receive-delivery-cta"
          className="w-full mb-6 rounded-2xl border-2 border-dashed border-brand/40 bg-brand-light/30 p-5 flex items-center gap-4 hover:border-brand transition-colors text-left">
          <div className="h-12 w-12 rounded-xl bg-brand text-white flex items-center justify-center"><Truck size={24} /></div>
          <div><div className="font-display font-bold text-slate-800">Receive a delivery</div>
            <div className="text-sm text-slate-500">Scan the note, record checks & temperatures, accept or reject.</div></div>
          <ChevronRight className="ml-auto text-brand" />
        </button>
      )}

      <div className="flex gap-2 flex-wrap mb-5" data-testid="delivery-filters">
        {[{ v: "all", l: "All" }, { v: "accepted", l: "Accepted" }, { v: "rejected", l: "Rejected" }, { v: "exceptions", l: "Exceptions" }].map((t) => (
          <button key={t.v} onClick={() => setFilter(t.v)} data-testid={`delivery-filter-${t.v}`}
            className={`text-sm px-3.5 py-1.5 rounded-full font-medium transition-colors ${filter === t.v ? "bg-brand text-white" : "bg-white border border-slate-200 text-slate-600"}`}>{t.l}</button>
        ))}
      </div>

      {filtered.length === 0 ? <EmptyState icon={Truck} title="No deliveries recorded yet." /> :
        <div className="space-y-2" data-testid="delivery-list">
          {filtered.map((d) => {
            const rejected = d.acceptance === "rejected";
            const exc = d.has_temperature_exception;
            return (
              <div key={d.delivery_id} className={`cc-card p-4 flex items-center justify-between ${rejected || exc ? "border-red-200 bg-red-50/40" : ""}`} data-testid={`delivery-${d.delivery_id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${rejected ? "bg-red-100" : exc ? "bg-amber-100" : "bg-brand-light"}`}>
                    {rejected ? <XCircle className="text-red-500" size={20} /> : exc ? <AlertTriangle className="text-amber-500" size={20} /> : <CheckCircle2 className="text-brand-dark" size={20} />}
                  </div>
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-800 truncate">{d.supplier_name}{d.document?.reference?.document_number ? ` · ${d.document.reference.document_number}` : ""}</div>
                    <div className="text-xs text-slate-500 truncate">
                      {acceptanceLabel(d.acceptance)}{d.temperature_count ? ` · ${d.temperature_count} temp${d.temperature_count > 1 ? "s" : ""}` : ""}{d.ai_assisted ? " · AI-assisted" : ""} · {d.received_by_name} · {fmtDateTime(d.received_at)}
                    </div>
                    {d.corrective_action && <div className="text-xs text-slate-500 italic mt-0.5 truncate">"{d.corrective_action}"</div>}
                  </div>
                </div>
                <StatusBadge status={rejected ? "exception" : exc ? "exception" : "passed"} />
              </div>
            );
          })}
        </div>}

      {receiving && <ReceiveDeliveryModal suppliers={suppliers} aiAvailable={aiAvailable} siteId={siteId} onClose={() => setReceiving(false)} onSaved={onSaved} />}
    </div>
  );
}
